"use client"

import InfiniteScroll from "react-infinite-scroller"

import { CollectorLoader } from "components/Placeholder"
import { useEffect, useRef, useState } from "react"
import { Collectible, EnrichedCollectRecord } from "@/types/collectibles"
import { getCollectors } from "@/lib/api/collectibles"
import CollectorTable from "@/components/Collectibles/CollectorTable"
import { DialogTitle } from "@/components/ui/dialog"

/**
 * Placeholders for collectors, when loading.
 */
function Placeholders(props: { total: number }) {
  const { total } = props
  const Placeholders = []
  for (let i = 0; i < Math.min(25, total); i++) {
    Placeholders.push(
      <>
        <CollectorLoader />
      </>
    )
  }
  return (
    <>
      <div className="px-4">
        <div className="flow-root">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle">
              <table className="min-w-full divide-y divide-gray-300">
                <tbody className="divide-y dark:divide-gray-800 divide-gray-200 bg-background text-left">
                  {Placeholders}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * Modal for displaying collectors of a collectible.
 *
 * This contains all client logic (eg hooks and datafetching) for the modal.
 */
export default function CollectorsModalClient({
  postCollectible,
  totalCollectors,
  children,
}: {
  postCollectible: Collectible
  totalCollectors: number
  children?: React.ReactNode
}) {
  const [records, setRecords] = useState<Array<EnrichedCollectRecord>>([])
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const scrollParentRef = useRef<HTMLDivElement | null>(null)

  async function _fetchData(page: number) {
    console.log("FETCHING NEW INFINITE DATA!", page)
    if (!postCollectible || isFetching) return
    setIsFetching(true)
    const lastCreatedAt =
      records.length > 0 ? records[records.length - 1]?.collectedAt : undefined
    const newRecords = await getCollectors(
      postCollectible.id,
      20,
      lastCreatedAt
    )

    const combined = [...records, ...newRecords]

    console.log("FETCHED NEW INFINITE DATA!", combined)

    setRecords(combined)
    setIsFetching(false)
  }

  // Fetch initial data when modal is opened.
  useEffect(() => {
    if (!open) return
    _fetchData(0)
  }, [])

  /**
   * Unfortunately we only support this for new collectibles, since we
   * only began emitting onchain events for new ones.
   */
  if (postCollectible?.version === 0 || !postCollectible) {
    return null
  }

  const title = <>{totalCollectors} Collectors</>

  const content = (
    <InfiniteScroll
      hasMore={records?.length < totalCollectors}
      // On initial page load, display the min{20, initialTotal} placeholders.
      // Then, when the user scrolls, only display one.
      loader={
        <Placeholders
          total={records.length === 0 ? Math.min(20, totalCollectors) : 1}
          key="loader"
        />
      }
      loadMore={_fetchData}
      useWindow={false}
      initialLoad={false}
      getScrollParent={() => scrollParentRef.current}
    >
      <CollectorTable records={records} />
    </InfiniteScroll>
  )

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      {children}
      {content}
    </>
  )
}
